import { useState } from 'react';
import {
  Color, Size, badgeNumberProblem, clockCredentialRevokePath, clockCredentialsPath, t,
  type ActiveCredentialRow, type ClockCredentialDraft, type IssuedCredential,
} from '@bms2/shared';
import { Button, TextInput } from '../../components/controls';
import { FormFrame } from '../../components/forms/FormFrame';
import { useDateFormat, useViewerZone } from '../../shell/ViewerZone';
import { whenText } from '../../shell/viewerDates';
import { post } from '../records/api';
import styles from './HrPages.module.css';

/**
 * Register Badge (T2069): the number the badge carries, stored only as a hash
 * through piece 15's issuing service.
 */
function RegisterBadgeWindow({ apiBaseUrl, employmentId, onClose, onSaved }: { apiBaseUrl: string; employmentId: string; onClose: () => void; onSaved: () => void }) {
  const [badgeNumber, setBadgeNumber] = useState('');
  const [refusal, setRefusal] = useState<string | null>(null);
  const problem = badgeNumberProblem(badgeNumber);
  const save = async () => {
    const draft: ClockCredentialDraft = { credentialKind: 'Badge', badgeNumber };
    const answer = await post<IssuedCredential>(clockCredentialsPath(employmentId), draft, apiBaseUrl);
    if (!answer.ok) return setRefusal(answer.message);
    onSaved();
  };
  return (
    <FormFrame
      host="window"
      open
      title={t('hr.credentials.badgeTitle')}
      width="regular"
      dataForm="register-badge"
      typed={badgeNumber !== ''}
      onCancel={onClose}
      refusal={refusal}
      onDismissRefusal={() => setRefusal(null)}
      act={{ label: t('hr.credentials.badgeAct'), onAct: () => save(), disabled: problem !== null, disabledReason: problem ?? '' }}
    >
      <div className={styles.fields} data-register-badge={employmentId}>
        <p className={styles.meta}>{t('hr.credentials.badgeLead')}</p>
        <TextInput size={Size.Medium} label={t('hr.credentials.badgeNumber')} value={badgeNumber} onChange={setBadgeNumber} required name="badgeNumber" />
      </div>
    </FormFrame>
  );
}

/**
 * The Access section's clock credentials (piece 34; T2069): the job's active
 * credentials, each with Revoke, and Issue PIN and Register Badge. 🔒 A PIN
 * the server answers is held in this component's state alone, shown once, and
 * gone when Done is pressed or the page is left; it is never logged.
 */
export function ClockCredentials({ apiBaseUrl, employmentId, credentials, onChanged }: {
  apiBaseUrl: string;
  employmentId: string;
  credentials: ActiveCredentialRow[];
  onChanged: () => void;
}) {
  const zone = useViewerZone();
  const format = useDateFormat();
  const [pin, setPin] = useState<string | null>(null);
  const [badgeOpen, setBadgeOpen] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const holdsPin = credentials.some((row) => row.credentialKind === 'PIN');
  const issuePin = async () => {
    const draft: ClockCredentialDraft = { credentialKind: 'PIN' };
    const answer = await post<IssuedCredential>(clockCredentialsPath(employmentId), draft, apiBaseUrl);
    if (!answer.ok) return setRefusal(answer.message);
    setRefusal(null);
    setPin(answer.data.pin);
    onChanged();
  };
  const revoke = async (row: ActiveCredentialRow) => {
    const answer = await post<{ id: string }>(clockCredentialRevokePath(employmentId, row.id), {}, apiBaseUrl);
    if (!answer.ok) return setRefusal(answer.message);
    setRefusal(null);
    onChanged();
  };
  return (
    <div className={styles.fields} data-employee-credentials={employmentId}>
      {credentials.length === 0 ? <p className={styles.meta}>{t('hr.credentials.none')}</p> : null}
      <ul className={styles.list}>
        {credentials.map((row) => (
          <li key={row.id} className={styles.item} data-credential-row={row.id}>
            <span className={styles.itemMain}>
              <span>{row.credentialKind}</span>
              <span className={styles.meta}>{[t('hr.credentials.issued'), whenText(row.issuedAt, zone, format)].join(' ')}</span>
            </span>
            <span data-credential-revoke={row.id}>
              <Button size={Size.Small} color={Color.Inactive} text={[t('hr.credentials.revoke')]} actions={[() => void revoke(row)]} />
            </span>
          </li>
        ))}
      </ul>
      {pin ? (
        <div className={styles.controls} data-employee-pin="shown">
          <span className={styles.itemMain}>
            <span>{[t('hr.credentials.pinLabel'), pin].join(' ')}</span>
            <span className={styles.meta}>{t('hr.credentials.pinShown')}</span>
          </span>
          <Button size={Size.Small} color={Color.Navy} text={[t('hr.credentials.done')]} actions={[() => setPin(null)]} />
        </div>
      ) : null}
      {refusal ? <p className={styles.meta} data-employee-credentials-refusal="true">{refusal}</p> : null}
      <div className={styles.controls}>
        <span data-credential-issue="pin">
          <Button size={Size.Small} color={Color.Navy} text={[t(holdsPin ? 'hr.credentials.issueNewPin' : 'hr.credentials.issuePin')]} actions={[() => void issuePin()]} />
        </span>
        <span data-credential-issue="badge">
          <Button size={Size.Small} color={Color.Navy} text={[t('hr.credentials.registerBadge')]} actions={[() => setBadgeOpen(true)]} />
        </span>
      </div>
      {badgeOpen ? (
        <RegisterBadgeWindow
          apiBaseUrl={apiBaseUrl}
          employmentId={employmentId}
          onClose={() => setBadgeOpen(false)}
          onSaved={() => {
            setBadgeOpen(false);
            onChanged();
          }}
        />
      ) : null}
    </div>
  );
}
